import { useState, useEffect, useCallback, type FormEvent } from "react";
import {
  listUsers,
  createUser,
  activateUser,
  updateUser,
  listGroups,
  assignWhatsAppRole,
} from "../services/auth";
import type { User, UserCreateRequest, Group } from "../types/auth";
import { WHATSAPP_ROLE_CHOICES } from "../types/auth";

export default function UsersPage() {
  const [users, setUsers] = useState<User[]>([]);
  const [groups, setGroups] = useState<Group[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [successMsg, setSuccessMsg] = useState<string | null>(null);

  // Create user form
  const [showCreate, setShowCreate] = useState(false);
  const [newUser, setNewUser] = useState<UserCreateRequest>({
    username: "",
    email: "",
    password: "",
    first_name: "",
    last_name: "",
  });
  const [creating, setCreating] = useState(false);

  // Edit user popup
  const [editingUser, setEditingUser] = useState<User | null>(null);
  const [editForm, setEditForm] = useState({ first_name: "", last_name: "", email: "", is_staff: false });
  const [savingEdit, setSavingEdit] = useState(false);

  // Selected group assignment
  const [selectedGroup, setSelectedGroup] = useState<number | "">("");

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [usersData, groupsData] = await Promise.all([
        listUsers({ page_size: 100 }),
        listGroups({ page_size: 100 }),
      ]);
      setUsers(usersData.results || []);
      setGroups(groupsData.results || []);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Error al cargar datos");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleCreateUser = async (e: FormEvent) => {
    e.preventDefault();
    setCreating(true);
    setError(null);
    setSuccessMsg(null);
    try {
      await createUser(newUser);
      setSuccessMsg(`Usuario "${newUser.username}" creado exitosamente`);
      setShowCreate(false);
      setNewUser({ username: "", email: "", password: "", first_name: "", last_name: "" });
      await fetchData();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Error al crear usuario");
    } finally {
      setCreating(false);
    }
  };

  const openEditPopup = (user: User) => {
    setEditingUser(user);
    setEditForm({ first_name: user.first_name || "", last_name: user.last_name || "", email: user.email || "", is_staff: user.is_staff });
  };

  const handleSaveEdit = async () => {
    if (!editingUser) return;
    setSavingEdit(true);
    setError(null);
    try {
      await updateUser(editingUser.id, editForm);
      setSuccessMsg(`Usuario "${editingUser.username}" actualizado`);
      setEditingUser(null);
      await fetchData();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Error al actualizar usuario");
    } finally {
      setSavingEdit(false);
    }
  };

  const handleDeleteUser = async (user: User) => {
    if (!confirm(`¿Desactivar a "${user.username}"?`)) return;
    try {
      await activateUser(user.id, { is_active: false });
      setSuccessMsg(`Usuario "${user.username}" desactivado`);
      await fetchData();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Error al desactivar usuario");
    }
  };

  const handleToggleActive = async (user: User) => {
    try {
      await activateUser(user.id, { is_active: !user.is_active });
      setSuccessMsg(`Usuario "${user.username}" ${!user.is_active ? "activado" : "desactivado"}`);
      await fetchData();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Error al cambiar estado");
    }
  };

  const handleAssignGroup = async (userId: number, groupId: number | "") => {
    if (groupId === "") return;
    try {
      await updateUser(userId, { groups: [groupId] });
      setSuccessMsg("Rol asignado correctamente");
      setSelectedGroup("");
      await fetchData();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Error al asignar rol");
    }
  };

  const handleToggleStaff = async (user: User) => {
    try {
      await updateUser(user.id, { is_staff: !user.is_staff });
      setSuccessMsg(`Usuario "${user.username}" actualizado`);
      await fetchData();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Error al actualizar usuario");
    }
  };

  const handleWhatsAppRole = async (user: User, rol: string, action: 'assign' | 'unassign') => {
    try {
      await assignWhatsAppRole({ user_id: user.id, rol, action });
      setSuccessMsg(`Rol WhatsApp "${rol}" ${action === 'assign' ? 'asignado a' : 'quitado de'} ${user.username}`);
      await fetchData();
    } catch (err) {
      setError(err instanceof Error ? err.message : `Error al ${action === 'assign' ? 'asignar' : 'quitar'} rol`);
    }
  };

  const hasWhatsAppRole = (user: User, rol: string): boolean => {
    return user.whatsapp_roles?.some(r => r.rol === rol) ?? false;
  };

  return (
    <div className="page-container">
      <div className="page-header">
        <div>
          <h2 className="page-title">Gestión de Usuarios</h2>
          <p className="page-subtitle">
            {users.length} usuario{users.length !== 1 ? "s" : ""} registrado{users.length !== 1 ? "s" : ""}
          </p>
        </div>
        <button className="btn btn-primary" onClick={() => setShowCreate(!showCreate)}>
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <line x1="12" y1="5" x2="12" y2="19" />
            <line x1="5" y1="12" x2="19" y2="12" />
          </svg>
          Nuevo Usuario
        </button>
      </div>

      {error && <div className="alert alert-error">{error}</div>}
      {successMsg && <div className="alert alert-success">{successMsg}</div>}

      {showCreate && (
        <div className="card form-card">
          <h3>Crear nuevo usuario</h3>
          <form onSubmit={handleCreateUser} className="create-user-form">
            <div className="form-row three-col">
              <div className="form-group">
                <label>Usuario *</label>
                <input
                  type="text"
                  value={newUser.username}
                  onChange={(e) => setNewUser({ ...newUser, username: e.target.value })}
                  placeholder="Nombre de usuario"
                  required
                />
              </div>
              <div className="form-group">
                <label>Email *</label>
                <input
                  type="email"
                  value={newUser.email}
                  onChange={(e) => setNewUser({ ...newUser, email: e.target.value })}
                  placeholder="correo@ejemplo.com"
                  required
                />
              </div>
              <div className="form-group">
                <label>Contraseña *</label>
                <input
                  type="password"
                  value={newUser.password}
                  onChange={(e) => setNewUser({ ...newUser, password: e.target.value })}
                  placeholder="Contraseña"
                  required
                />
              </div>
            </div>
            <div className="form-row two-col">
              <div className="form-group">
                <label>Nombres</label>
                <input
                  type="text"
                  value={newUser.first_name || ""}
                  onChange={(e) => setNewUser({ ...newUser, first_name: e.target.value })}
                  placeholder="Nombres"
                />
              </div>
              <div className="form-group">
                <label>Apellidos</label>
                <input
                  type="text"
                  value={newUser.last_name || ""}
                  onChange={(e) => setNewUser({ ...newUser, last_name: e.target.value })}
                  placeholder="Apellidos"
                />
              </div>
            </div>
            <div className="form-row two-col">
              <div className="form-group">
                <label>Grupo/Rol inicial</label>
                <select
                  value={selectedGroup}
                  onChange={(e) => setSelectedGroup(e.target.value === "" ? "" : parseInt(e.target.value))}
                >
                  <option value="">Sin grupo</option>
                  {groups.map((g) => (
                    <option key={g.id} value={g.id}>{g.name}</option>
                  ))}
                </select>
              </div>
              <div className="form-group" />
            </div>
            <div className="form-actions">
              <button type="button" className="btn btn-secondary" onClick={() => setShowCreate(false)}>
                Cancelar
              </button>
              <button type="submit" className="btn btn-primary" disabled={creating}>
                {creating ? "Creando..." : "Crear Usuario"}
              </button>
            </div>
          </form>
        </div>
      )}

      {loading ? (
        <div className="loading-state">
          <div className="spinner" />
          <span>Cargando usuarios...</span>
        </div>
      ) : users.length === 0 ? (
        <div className="empty-state">
          <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="#64748b" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
            <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
            <circle cx="9" cy="7" r="4" />
            <path d="M23 21v-2a4 4 0 0 0-3-3.87" />
            <path d="M16 3.13a4 4 0 0 1 0 7.75" />
          </svg>
          <h3>No hay usuarios registrados</h3>
          <p>Crea el primer usuario para comenzar</p>
        </div>
      ) : (
        <div className="table-responsive">
          <table className="users-table">
            <thead>
              <tr>
                <th>Usuario</th>
                <th>Email</th>
                <th>Admin</th>
                <th>Activo</th>
                <th>Roles WhatsApp</th>
                <th>Grupo</th>
                <th>Acciones</th>
              </tr>
            </thead>
            <tbody>
              {users.map((user) => (
                <tr key={user.id}>
                  <td>
                    <strong>{user.username}</strong>
                  </td>
                  <td>{user.email}</td>
                  <td>
                    <label className="toggle-switch">
                      <input
                        type="checkbox"
                        checked={user.is_staff}
                        onChange={() => handleToggleStaff(user)}
                      />
                      <span className="toggle-slider"></span>
                    </label>
                  </td>
                  <td>
                    <label className="toggle-switch">
                      <input
                        type="checkbox"
                        checked={user.is_active}
                        onChange={() => handleToggleActive(user)}
                      />
                      <span className="toggle-slider"></span>
                    </label>
                  </td>
                  <td>
                    <div className="whatsapp-roles-actions">
                      {WHATSAPP_ROLE_CHOICES.map((role) => {
                        const assigned = hasWhatsAppRole(user, role.value);
                        return (
                          <button
                            key={role.value}
                            className={`role-chip ${assigned ? 'role-chip-active' : 'role-chip-inactive'}`}
                            onClick={() => handleWhatsAppRole(user, role.value, assigned ? 'unassign' : 'assign')}
                            title={`${assigned ? 'Quitar' : 'Asignar'} rol ${role.label}`}
                          >
                            {role.label}
                            {assigned ? ' ✓' : ' +'}
                          </button>
                        );
                      })}
                      {(!user.whatsapp_roles || user.whatsapp_roles.length === 0) && (
                        <span className="text-muted text-xs">Sin roles</span>
                      )}
                    </div>
                  </td>
                  <td>
                    <div className="table-actions">
                      <select
                        className="table-select"
                        value=""
                        onChange={(e) => {
                          const val = e.target.value;
                          if (val) {
                            handleAssignGroup(user.id, parseInt(val));
                          }
                        }}
                      >
                        <option value="">Asignar grupo</option>
                        {groups.map((g) => (
                          <option key={g.id} value={g.id}>{g.name}</option>
                        ))}
                      </select>
                    </div>
                  </td>
                  <td>
                    <div className="table-actions-row">
                      <button className="btn btn-sm btn-secondary" onClick={() => openEditPopup(user)} title="Editar">
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/></svg>
                      </button>
                      <button className="btn btn-sm btn-danger" onClick={() => handleDeleteUser(user)} title="Desactivar">
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="3 6 5 6 21 6"/><path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"/></svg>
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Edit user popup */}
      {editingUser && (
        <div className="modal-overlay" onClick={() => setEditingUser(null)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Editar: {editingUser.username}</h3>
              <button className="modal-close" onClick={() => setEditingUser(null)}>×</button>
            </div>
            <div className="modal-body">
              <div className="form-group">
                <label>Email</label>
                <input type="email" value={editForm.email} onChange={(e) => setEditForm({ ...editForm, email: e.target.value })} />
              </div>
              <div className="form-group">
                <label>Nombres</label>
                <input type="text" value={editForm.first_name} onChange={(e) => setEditForm({ ...editForm, first_name: e.target.value })} />
              </div>
              <div className="form-group">
                <label>Apellidos</label>
                <input type="text" value={editForm.last_name} onChange={(e) => setEditForm({ ...editForm, last_name: e.target.value })} />
              </div>
              <div className="form-group">
                <label className="checkbox-label">
                  <input type="checkbox" checked={editForm.is_staff} onChange={(e) => setEditForm({ ...editForm, is_staff: e.target.checked })} />
                  Administrador
                </label>
              </div>
            </div>
            <div className="modal-actions">
              <button className="btn btn-secondary" onClick={() => setEditingUser(null)}>Cancelar</button>
              <button className="btn btn-primary" onClick={handleSaveEdit} disabled={savingEdit}>
                {savingEdit ? "Guardando..." : "Guardar"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}